import { ReadinessModel, RouteClass, } from "../contracts/common.js";
import {} from "../contracts/result.js";
import { expectArray, expectBoolean, expectNumber, expectObject, expectOptionalNumber, expectOptionalString, expectString, parseLivePage, readField, readOptionalField, } from "../internal/json.js";
import { createQuery } from "../internal/query.js";
import { requestJsonResult } from "../internal/transport.js";
import {} from "./transactions.js";
function readOptionalAliasField(object, names) {
    for (const name of names) {
        const value = readOptionalField(object, name);
        if (value !== undefined) {
            return value;
        }
    }
    return undefined;
}
const MEMPOOL_ROUTE = {
    method: "GET",
    name: "mempool",
    path: "/api/v1/mempool",
    readinessModel: ReadinessModel.LIVE,
    routeClass: RouteClass.LIVE,
};
const MEMPOOL_TRANSACTIONS_ROUTE = {
    method: "GET",
    name: "mempoolTransactions",
    path: "/api/v1/mempool/txs",
    readinessModel: ReadinessModel.LIVE,
    routeClass: RouteClass.LIVE,
};
const NETWORK_ROUTE = {
    method: "GET",
    name: "network",
    path: "/api/v1/network",
    readinessModel: ReadinessModel.LIVE,
    routeClass: RouteClass.LIVE,
};
/**
 * Public route descriptors for live JSON routes.
 */
export const liveRouteDescriptors = {
    mempool: MEMPOOL_ROUTE,
    mempoolTransactions: MEMPOOL_TRANSACTIONS_ROUTE,
    network: NETWORK_ROUTE,
};
function parseTransactionTags(value, path) {
    const object = expectObject(value, path);
    return {
        mined: expectBoolean(readField(object, "mined", path), `${path}.mined`),
        coinbase: expectBoolean(readField(object, "coinbase", path), `${path}.coinbase`),
    };
}
function parseStringArray(value, path) {
    return expectArray(value, path, expectString);
}
function parseMempoolSummary(value, path) {
    const object = expectObject(value, path);
    const usageBytes = expectOptionalNumber(readOptionalField(object, "usageBytes"), `${path}.usageBytes`);
    const maxBytes = expectOptionalNumber(readOptionalField(object, "maxBytes"), `${path}.maxBytes`);
    return {
        count: expectNumber(readField(object, "count", path), `${path}.count`),
        totalBytes: expectNumber(readField(object, "totalBytes", path), `${path}.totalBytes`),
        ...(usageBytes == null ? {} : { usageBytes }),
        ...(maxBytes == null ? {} : { maxBytes }),
        updatedAt: expectString(readField(object, "updatedAt", path), `${path}.updatedAt`),
        recentHashes: parseStringArray(readField(object, "recentHashes", path), `${path}.recentHashes`),
    };
}
function parseMempoolTransactionSummary(value, path) {
    const object = expectObject(value, path);
    return {
        txHash: expectString(readField(object, "txHash", path), `${path}.txHash`),
        txType: expectString(readField(object, "txType", path), `${path}.txType`),
        txSize: expectNumber(readField(object, "txSize", path), `${path}.txSize`),
        valueOut: expectNumber(readField(object, "valueOut", path), `${path}.valueOut`),
        firstSeenAt: expectString(readField(object, "firstSeenAt", path), `${path}.firstSeenAt`),
        lastSeenAt: expectString(readField(object, "lastSeenAt", path), `${path}.lastSeenAt`),
        tags: parseTransactionTags(readField(object, "tags", path), `${path}.tags`),
    };
}
function parseNetworkValidatorClassSummary(value, path) {
    const object = expectObject(value, path);
    const statusObject = expectObject(readField(object, "status", path), `${path}.status`);
    const status = {};
    for (const [key, raw] of Object.entries(statusObject)) {
        status[key] = expectNumber(raw, `${path}.status.${key}`);
    }
    return {
        total: expectNumber(readField(object, "total", path), `${path}.total`),
        status,
    };
}
function parseNetworkValidatorSummary(value, path) {
    const object = expectObject(value, path);
    return {
        regular: parseNetworkValidatorClassSummary(readField(object, "regular", path), `${path}.regular`),
        evonode: parseNetworkValidatorClassSummary(readField(object, "evonode", path), `${path}.evonode`),
    };
}
function parseNetworkInfo(value, path) {
    const object = expectObject(value, path);
    const version = expectOptionalNumber(readOptionalField(object, "version"), `${path}.version`);
    const subversion = expectOptionalString(readOptionalField(object, "subversion"), `${path}.subversion`);
    const protocolVersion = expectOptionalNumber(readOptionalAliasField(object, ["protocolVersion", "protocolversion"]), `${path}.protocolVersion`);
    const timeOffset = expectOptionalNumber(readOptionalAliasField(object, ["timeOffset", "timeoffset"]), `${path}.timeOffset`);
    const connections = expectOptionalNumber(readOptionalField(object, "connections"), `${path}.connections`);
    const relayFee = expectOptionalNumber(readOptionalAliasField(object, ["relayFee", "relayfee"]), `${path}.relayFee`);
    const warnings = expectOptionalString(readOptionalField(object, "warnings"), `${path}.warnings`);
    return {
        ...(version == null ? {} : { version }),
        ...(subversion == null ? {} : { subversion }),
        ...(protocolVersion == null ? {} : { protocolVersion }),
        ...(timeOffset == null ? {} : { timeOffset }),
        ...(connections == null ? {} : { connections }),
        ...(relayFee == null ? {} : { relayFee }),
        ...(warnings == null ? {} : { warnings }),
    };
}
function parsePeerInfo(value, path) {
    const object = expectObject(value, path);
    const id = expectOptionalNumber(readOptionalField(object, "id"), `${path}.id`);
    const addr = expectOptionalString(readOptionalField(object, "addr"), `${path}.addr`);
    const addrLocal = expectOptionalString(readOptionalAliasField(object, ["addrLocal", "addrlocal"]), `${path}.addrLocal`);
    const subversion = expectOptionalString(readOptionalAliasField(object, ["subversion", "subver"]), `${path}.subversion`);
    const inbound = readOptionalField(object, "inbound");
    const startingHeight = expectOptionalNumber(readOptionalAliasField(object, ["startingHeight", "startingheight"]), `${path}.startingHeight`);
    const syncedHeaders = expectOptionalNumber(readOptionalField(object, "syncedHeaders"), `${path}.syncedHeaders`);
    const syncedBlocks = expectOptionalNumber(readOptionalField(object, "syncedBlocks"), `${path}.syncedBlocks`);
    const connectionType = expectOptionalString(readOptionalField(object, "connectionType"), `${path}.connectionType`);
    return {
        ...(id == null ? {} : { id }),
        ...(addr == null ? {} : { addr }),
        ...(addrLocal == null ? {} : { addrLocal }),
        ...(subversion == null ? {} : { subversion }),
        ...(inbound == null ? {} : { inbound: expectBoolean(inbound, `${path}.inbound`) }),
        ...(startingHeight == null ? {} : { startingHeight }),
        ...(syncedHeaders == null ? {} : { syncedHeaders }),
        ...(syncedBlocks == null ? {} : { syncedBlocks }),
        ...(connectionType == null ? {} : { connectionType }),
    };
}
function parseNetworkSnapshot(value, path) {
    const object = expectObject(value, path);
    return {
        networkInfo: parseNetworkInfo(readField(object, "networkInfo", path), `${path}.networkInfo`),
        peerInfo: expectArray(readField(object, "peerInfo", path), `${path}.peerInfo`, parsePeerInfo),
        peerCount: expectNumber(readField(object, "peerCount", path), `${path}.peerCount`),
        validators: parseNetworkValidatorSummary(readField(object, "validators", path), `${path}.validators`),
        updatedAt: expectString(readField(object, "updatedAt", path), `${path}.updatedAt`),
    };
}
function createCursorQuery(params) {
    if (params == null) {
        return undefined;
    }
    const query = createQuery({
        cursor: params.cursor,
        limit: params.limit,
    });
    return query.size > 0 ? query : undefined;
}
/**
 * Build the live mempool and network route family for one client transport context.
 */
export function buildLiveRoutes(context) {
    return {
        async getMempoolSummary() {
            return requestJsonResult(context, MEMPOOL_ROUTE, MEMPOOL_ROUTE.path, parseMempoolSummary);
        },
        async getNetworkSnapshot() {
            return requestJsonResult(context, NETWORK_ROUTE, NETWORK_ROUTE.path, parseNetworkSnapshot);
        },
        async listMempoolTransactions(params) {
            const query = createCursorQuery(params);
            return requestJsonResult(context, MEMPOOL_TRANSACTIONS_ROUTE, MEMPOOL_TRANSACTIONS_ROUTE.path, (value, path) => parseLivePage(value, path, parseMempoolTransactionSummary), ...(query == null ? [] : [{ query }]));
        },
    };
}
//# sourceMappingURL=live.js.map